import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useEffect } from 'react';
import { Checkbox, Labeled } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import { FormField } from '~/components/atoms/FormField';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { PollFormMainSection } from './PollFormMainSection';

export type IPollFormValues = {
  title?: string;
  description?: string;
  options?: string;
  startDate?: string;
  endDate?: string;
  requires?: string;
  displayResults?: boolean;
  multipleChoices?: boolean;
  canUpdate?: boolean;
  inactive?: boolean;
};

export type IPollFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IPollFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    defaultValues?: DefaultValues<IPollFormValues>;
    onSubmit?: SubmitHandler<IPollFormValues>;
    onDisabledStatusChange?: (disabled: boolean) => void;
  };

export const PollForm: React.FC<IPollFormProps> = (props) => {
  const {
    sx,
    mode,
    defaultValues,
    onSubmit,
    onDisabledStatusChange,
    ...other
  } = props;
  const { t } = useTranslation();

  const form = usePersistentForm<IPollFormValues>({
    defaultValues,
  });
  useFormAutoFocus({ form, name: 'title' });

  const disabled = form.watch('inactive');
  useEffect(
    () => onDisabledStatusChange?.(!!disabled),
    [onDisabledStatusChange, disabled],
  );

  return (
    <ContentBlock.FormBody
      sx={sx}
      form={form}
      onSubmit={onSubmit}
      extraActions={
        <Labeled
          label={t('PollForm.fields.inactive.label')}
          labelPosition="right"
        >
          <FormField name="inactive" control={form.control} as={Checkbox} />
        </Labeled>
      }
      {...other}
    >
      <PollFormMainSection form={form} mode={mode} />
    </ContentBlock.FormBody>
  );
};
